/**
 * Clipping utility functions for WeevilTrak Map Component
 * Uses Turf.js for geospatial operations
 */

import * as turf from "@turf/turf";
import type { GeoJsonFeature } from "./types";
import type { Polygon, Feature } from "geojson";

/**
 * Create a Turf.js circle polygon from center point and radius
 * @param center - { lat, lng } center coordinates
 * @param radiusKm - radius in kilometers
 * @param steps - number of points to approximate circle (default: 64)
 * @returns Turf Feature<Polygon> representing the circle
 */
export const createCirclePolygon = (
    center: { lat: number; lng: number },
    radiusKm: number,
    steps = 64
): Feature<Polygon> => {
    return turf.circle(
        [center.lng, center.lat], // Turf uses [lng, lat] format
        radiusKm,
        { units: "kilometers", steps }
    );
};

/**
 * Calculate intersection between a polygon and a circle
 * @param polygonFeature - GeoJSON Feature representing the polygon
 * @param center - Circle center { lat, lng }
 * @param radiusKm - Circle radius in kilometers
 * @returns Intersected polygon or null if no intersection
 */
export const clipPolygonWithCircle = (
    polygonFeature: Feature<Polygon>,
    center: { lat: number; lng: number },
    radiusKm: number
): Feature<Polygon> | null => {
    try {
        // Create circle polygon
        const circlePolygon = createCirclePolygon(center, radiusKm);

        console.log("Clipping polygon with circle:", {
            polygonCoords: polygonFeature.geometry.coordinates,
            circleCoords: circlePolygon.geometry.coordinates,
            circleCenter: center,
            circleRadius: radiusKm
        });

        // Try using turf.intersect with a FeatureCollection containing both polygons
        const fc = turf.featureCollection([polygonFeature as any, circlePolygon as any]);
        const intersection = turf.intersect(fc as any);

        if (intersection) {
            console.log("Intersection result:", intersection.geometry.coordinates);
        } else {
            console.log("No intersection found");
        }

        return intersection as Feature<Polygon> | null;
    } catch (error) {
        // If no intersection or invalid geometry, return null
        console.error("Intersection error:", error);
        return null;
    }
};

/**
 * Process all GeoJSON features and clip them with circle boundary
 * @param features - Array of GeoJSON features to clip
 * @param center - Circle center { lat, lng }
 * @param radiusKm - Circle radius in kilometers
 * @returns Array of clipped GeoJSON features (only those with valid intersections)
 */
export const clipGeoJsonFeaturesWithCircle = (
    features: GeoJsonFeature[],
    center: { lat: number; lng: number },
    radiusKm: number
): GeoJsonFeature[] => {
    const clippedFeatures: GeoJsonFeature[] = [];

    features.forEach(feature => {
        // Clone feature to avoid mutating original
        const clonedFeature = JSON.parse(JSON.stringify(feature)) as GeoJsonFeature;

        // Convert to Turf feature
        const turfPolygon = clonedFeature as unknown as Feature<Polygon>;

        // Calculate intersection
        const intersection = clipPolygonWithCircle(turfPolygon, center, radiusKm);

        if (intersection && intersection.geometry) {
            console.log("Intersection geometry type:", intersection.geometry.type);
            console.log("Intersection coordinates:", intersection.geometry.coordinates);

            // Handle both Polygon and MultiPolygon results
            if (intersection.geometry.type === "Polygon") {
                // Preserve original properties
                const clippedFeature: GeoJsonFeature = {
                    type: "Feature",
                    properties: feature.properties,
                    geometry: {
                        type: "Polygon",
                        coordinates: intersection.geometry.coordinates
                    }
                };
                clippedFeatures.push(clippedFeature);
            } else if (intersection.geometry.type === "MultiPolygon") {
                // MultiPolygon - convert to multiple Polygon features
                intersection.geometry.coordinates.forEach((polygonCoords: any) => {
                    const clippedFeature: GeoJsonFeature = {
                        type: "Feature",
                        properties: feature.properties,
                        geometry: {
                            type: "Polygon",
                            coordinates: polygonCoords
                        }
                    };
                    clippedFeatures.push(clippedFeature);
                });
            }
        }
    });

    console.log("Total clipped features:", clippedFeatures.length);
    return clippedFeatures;
};

/**
 * Check if a point is inside the circle (for validation)
 * @param point - Point to check { lat, lng }
 * @param center - Circle center { lat, lng }
 * @param radiusKm - Circle radius in kilometers
 * @returns true if point is inside or on the circle boundary
 */
export const isPointInCircle = (
    point: { lat: number; lng: number },
    center: { lat: number; lng: number },
    radiusKm: number
): boolean => {
    const turfPoint = turf.point([point.lng, point.lat]);
    const turfCenter = turf.point([center.lng, center.lat]);
    const distance = turf.distance(turfPoint, turfCenter, { units: "kilometers" });
    return distance <= radiusKm;
};
